import { c as _c } from "react/compiler-runtime";
import { BilyTracking } from '../components/bily-tracking';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Layout } from '@/components/layout/Layout';
import { SecurityProvider } from '@/components/providers';
import { I18nProvider } from '@/context/I18nProvider';
import { useBilyIdentify } from '@/hooks/useBilyIdentify';
import { useDataFastIdentify } from '@/hooks/useDataFastIdentify';
import { useFacebookPixelIdentify } from '@/hooks/useFacebookPixelIdentify';
import { useFirstPromoterIdentify } from '@/hooks/useFirstPromoter';
import { useGoogleTranslateFix } from '@/hooks/useGoogleTranslateFix';
import { useGTMPageviews } from '@/hooks/useGTMPageviews';
import { useInitPostHog } from '@/hooks/useInitPostHog';
import { useSilenceExtensionErrors } from '@/hooks/useSilenceExtensionErrors';
import { FacebookErrorBoundary } from '@/lib/whatsapp/FacebookErrorBoundary';
import { restoreSearchIndexFromStorage } from '@/lib/search-index-persist';
import '@/styles/globals.css';
import '@/styles/dark-remap.css';
import { ClerkProvider } from '@clerk/nextjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { useSyncThemeFromAccount } from '@/hooks/useThemePreference';
import type { AppProps } from 'next/app';
import type { NextWebVitalsMetric } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { PostHogProvider } from 'posthog-js/react';
import { ReactNode, Suspense, useEffect, useState } from 'react';

/**
 * Reports Core Web Vitals and Next.js-specific metrics to PostHog.
 * Next.js calls this automatically for every navigation.
 * PostHog is lazy-loaded, so early metrics are queued and sent once ready.
 */
export function reportWebVitals(metric: NextWebVitalsMetric) {
  import('posthog-js').then(({
    default: posthog
  }) => {
    try {
      posthog.capture('web_vital', {
        $web_vital_name: metric.name,
        $web_vital_value: metric.value,
        metric_id: metric.id,
        metric_label: metric.label
      });
    } catch {
      // Silently fail if PostHog is blocked
    }
  }).catch(() => {
    // Silently fail if PostHog can't be loaded
  });
}
const ReactQueryDevtools = dynamic(() => import('@tanstack/react-query-devtools/build/modern/production.js').then(mod => mod.ReactQueryDevtools), {
  ssr: false
});

// Note: Console interceptors are installed via instrumentation.ts at server startup
// This ensures logs work from the very first request

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    }
  }
});

// Restore search index from localStorage on app start (before any component mounts).
// If cached data exists and is < 24h old, it's hydrated into the QueryClient cache,
// so the conversations page shows instant search without a full re-fetch.
restoreSearchIndexFromStorage(queryClient);

// Must be rendered inside ClerkProvider since it uses useUser()
function FirstPromoterTracker(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  useFirstPromoterIdentify();
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// Must be rendered inside ClerkProvider since it uses useUser().
// Re-inits the Facebook Pixel with Advanced Matching (em/ph) once we know
// the authenticated user — boosts match rate in Ads Manager.
function FacebookPixelTracker(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  useFacebookPixelIdentify();
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// Must be rendered inside ClerkProvider since it uses useUser()/useAuth().
// Emits Bily `Login` once per verified Clerk session and refreshes the customer
// context on a workspace switch.
function BilyIdentifyTracker(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  useBilyIdentify();
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// Must be rendered inside ClerkProvider since it uses useUser()/useAuth().
// Names the DataFast visitor once the Clerk session is verified, so the
// anonymous pageviews that preceded sign-up stay attached to the person.
function DataFastIdentifyTracker(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  useDataFastIdentify();
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// ------------------------------------------------------------

function useShowDevtools() {
  const $ = _c(2);
  const [show, setShow] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setShow(localStorage.getItem("rq-devtools") === "true");
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return show;
}

/** Applies the theme saved on the signed-in user's account. */
const ThemeAccountSync = () => {
  useSyncThemeFromAccount();
  return null;
};
export default function App(t0) {
  const $ = _c(21);
  const {
    Component,
    pageProps
  } = t0;
  useGoogleTranslateFix();
  useSilenceExtensionErrors();
  useGTMPageviews();
  const posthogClient = useInitPostHog();
  const showDevtools = useShowDevtools();
  const router = useRouter();
  let t1;
  if ($[0] !== router.pathname) {
    t1 = router.pathname.startsWith("/sign-in") || router.pathname.startsWith("/sign-up") || router.pathname === "/sso-callback" || router.pathname === "/forgot-password" || router.pathname === "/organizations/create";
    $[0] = router.pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isAuthPage = t1;
  const isStandalonePage = isAuthPage;
  let t2;
  if ($[2] !== showDevtools) {
    t2 = showDevtools && <ReactQueryDevtools initialIsOpen={false} />;
    $[2] = showDevtools;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isAuthPage ? "light" : undefined;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ThemeAccountSync />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== Component || $[6] !== isStandalonePage || $[7] !== pageProps) {
    t5 = <ClerkProvider signInUrl={process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL || "/sign-in"} signUpUrl={process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL || "/sign-up"} afterSignOutUrl={process.env.NEXT_PUBLIC_CLERK_AFTER_SIGN_OUT_URL || "/sign-in"} signInFallbackRedirectUrl="/conversations" signUpFallbackRedirectUrl="/conversations">{t4}<FirstPromoterTracker><FacebookPixelTracker><BilyIdentifyTracker><DataFastIdentifyTracker>{isStandalonePage ? <Component {...pageProps} /> : <SecurityProvider><Layout><Component {...pageProps} /></Layout></SecurityProvider>}</DataFastIdentifyTracker></BilyIdentifyTracker></FacebookPixelTracker></FirstPromoterTracker></ClerkProvider>;
    $[5] = Component;
    $[6] = isStandalonePage;
    $[7] = pageProps;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <ThemeProvider attribute="class" defaultTheme="light" enableSystem={true} disableTransitionOnChange={true} forcedTheme={t3}>{t5}</ThemeProvider>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t6) {
    t7 = <QueryClientProvider client={queryClient}>{t2}{t6}</QueryClientProvider>;
    $[12] = t2;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const content = t7;
  let t10;
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Suspense fallback={null}><BilyTracking /></Suspense>;
    t9 = <FacebookErrorBoundary />;
    t10 = <Script data-website-id="dfid_G5VaQWV4BPIZKLvtFTbQO" data-domain="orsay.ai" src="https://datafa.st/js/script.js" strategy="afterInteractive" />;
    $[15] = t10;
    $[16] = t8;
    $[17] = t9;
  } else {
    t10 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[18] !== content || $[19] !== posthogClient) {
    t11 = <ErrorBoundary>{t8}{t9}{t10}<I18nProvider>{posthogClient ? <PostHogProvider client={posthogClient}>{content}</PostHogProvider> : content}</I18nProvider></ErrorBoundary>;
    $[18] = content;
    $[19] = posthogClient;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}